/**
 * Settings tab — last of Phase 5.
 *
 * Three sections:
 *   1. Connection — read-only cpid + wsUrl, editable displayName.
 *   2. Configuration — grouped key editor backed by useConfigurationStore.
 *      First visit seeds the table from DEFAULT_CONFIGURATION_KEYS_V16.
 *   3. Maintenance — Clear all stale transactions, Soft reset, Full reset.
 *
 * TopologyEditor and Advanced lock-timing inputs from plan.md are
 * intentionally deferred — they require a wider Topology persistence story
 * that we don't have a Dexie writer for yet.
 */
import { useEffect, useState } from 'react';
import { useStationStore } from '@/state/useStationStore';
import { useConfigurationStore } from '@/state/useConfigurationStore';
import { useRecoveryStore } from '@/state/useRecoveryStore';
import { pluralize } from '@/lib/format';
import { ConfigurationTable } from '@/ui/components/ConfigurationTable';
import { TopologyEditor } from '@/ui/components/TopologyEditor';
import { LocalAuthListSection } from '@/ui/components/LocalAuthListSection';
import { FirmwareSection } from '@/ui/components/FirmwareSection';
import { DiagnosticsSection } from '@/ui/components/DiagnosticsSection';
import { useConfirm } from '@/hooks/useConfirm';
import { toast } from '@/state/useToastStore';
import { isV2x, subprotocolFor } from '@/lib/ocppProfile';

export function SettingsTab(): JSX.Element {
  const stationId = useStationStore((s) => s.stationId);
  const cpid = useStationStore((s) => s.cpid);
  const wsUrl = useStationStore((s) => s.wsUrl);
  const persistedDisplayName = useStationStore((s) => s.displayName);
  const ocppVersion = useStationStore((s) => s.ocppVersion);
  const connectionState = useStationStore((s) => s.connectionState);
  const setDisplayName = useStationStore((s) => s.setDisplayName);
  const softReset = useStationStore((s) => s.softReset);
  const fireDiagnosticEvent = useStationStore((s) => s.fireDiagnosticEvent);
  const fullReset = useStationStore((s) => s.fullReset);
  const refresh = useConfigurationStore((s) => s.refresh);
  const entries = useConfigurationStore((s) => s.entries);
  const setValue = useConfigurationStore((s) => s.setValue);
  const resetToDefault = useConfigurationStore((s) => s.resetToDefault);
  const deleteKey = useConfigurationStore((s) => s.deleteKey);
  const scanRecovery = useRecoveryStore((s) => s.scan);
  const clearAllStale = useRecoveryStore((s) => s.clearAllStale);

  // Session 13 — hydrate the input from the store so the bug where a
  // saved Display name doesn't render on next tab visit is fixed.
  // Re-sync whenever the persisted value changes (different station,
  // remote ChangeConfiguration, …).
  const [displayName, setDisplayNameLocal] = useState(persistedDisplayName ?? '');
  useEffect(() => {
    setDisplayNameLocal(persistedDisplayName ?? '');
  }, [persistedDisplayName]);

  const [busy, setBusy] = useState(false);
  const [clearedCount, setClearedCount] = useState<number | null>(null);
  const confirm = useConfirm();

  // Session 18 — pass evses so the derived NumberOfConnectors row
  // reflects the live topology when the Settings tab opens.
  const evses = useStationStore((s) => s.evses);
  useEffect(() => {
    void refresh(stationId, ocppVersion, evses);
  }, [refresh, stationId, ocppVersion, evses]);

  // Session 13 — editable identity (cpid + wsUrl) only when disconnected.
  // Identity is the (cpid, wsUrl) Dexie compound key, so an in-flight
  // WS lock would race a rename. RegistryPage owns the canonical
  // update path; the Settings inline edit calls the same store action.
  const isConnected = connectionState !== 'Disconnected' && connectionState !== 'Error';
  const updateStation = useStationStore((s) => s.updateIdentity);
  const [identityEdit, setIdentityEdit] = useState<
    null | { cpid: string; wsUrl: string }
  >(null);
  const [identityError, setIdentityError] = useState<string | null>(null);

  const handleSaveName = (): void => {
    void setDisplayName(displayName.trim() === '' ? null : displayName.trim()).then(() => {
      toast('Display name saved');
    });
  };

  const handleClearStale = (): void => {
    confirm('mark every open transaction as orphaned', () => {
      setClearedCount(null);
      setBusy(true);
      void (async () => {
        try {
          const found = await scanRecovery(stationId);
          await clearAllStale();
          setClearedCount(found.length);
          toast(`${found.length} transaction(s) marked orphaned`);
        } finally {
          setBusy(false);
        }
      })();
    });
  };

  const handleSoftReset = (): void => {
    confirm('clear in-tab state and open transactions for this station', () => {
      void softReset().then(() => toast('Soft reset complete'));
    });
  };

  const handleFullReset = (): void => {
    confirm(
      'delete this station and ALL its data (profiles, transactions, events) — this cannot be undone',
      () => {
        if (!window.confirm('Are you sure? Type-Yes-confirm equivalent.')) return;
        void fullReset().then(() => {
          window.location.assign('/');
        });
      },
    );
  };

  return (
    <div
      role="tabpanel"
      id="tab-panel-settings"
      aria-labelledby="tab-settings"
      className="space-y-4 p-4"
      data-testid="settings-tab"
    >
      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <h2 className="mb-2 text-sm font-medium text-slate-100">Connection</h2>
        {identityEdit === null ? (
          <dl className="grid grid-cols-[7rem_1fr_auto] gap-x-3 gap-y-1 text-xs">
            <dt className="text-slate-500">CPID</dt>
            <dd className="font-mono text-slate-200" data-testid="settings-cpid">
              {cpid}
            </dd>
            <dd className="row-span-2 self-start">
              <button
                type="button"
                disabled={isConnected}
                onClick={() => setIdentityEdit({ cpid, wsUrl })}
                title={
                  isConnected
                    ? 'Disconnect first — identity cannot change while the WebSocket is open.'
                    : 'Edit cpid + wsUrl'
                }
                className="rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="settings-identity-edit"
              >
                Edit
              </button>
            </dd>
            <dt className="text-slate-500">WS URL</dt>
            <dd className="break-all font-mono text-slate-200" data-testid="settings-wsurl">
              {wsUrl}
            </dd>
            <dt className="text-slate-500">Version</dt>
            <dd
              className="col-span-2 font-mono text-slate-200"
              data-testid="settings-ocpp-version"
            >
              OCPP {ocppVersion}{' '}
              <span className="text-slate-500">
                · subprotocol{' '}
                <code className="text-slate-300">
                  {subprotocolFor(ocppVersion)}
                </code>
              </span>
            </dd>
          </dl>
        ) : (
          <div className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-2 text-xs">
            <label
              className="self-center text-slate-500"
              htmlFor="settings-cpid-input"
            >
              CPID
            </label>
            <input
              id="settings-cpid-input"
              type="text"
              value={identityEdit.cpid}
              onChange={(e) =>
                setIdentityEdit({ ...identityEdit, cpid: e.target.value })
              }
              className="rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
              data-testid="settings-cpid-input"
            />
            <label
              className="self-center text-slate-500"
              htmlFor="settings-wsurl-input"
            >
              WS URL
            </label>
            <input
              id="settings-wsurl-input"
              type="text"
              value={identityEdit.wsUrl}
              onChange={(e) =>
                setIdentityEdit({ ...identityEdit, wsUrl: e.target.value })
              }
              className="rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
              data-testid="settings-wsurl-input"
            />
            <div className="col-span-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setIdentityError(null);
                  void updateStation({
                    cpid: identityEdit.cpid.trim(),
                    wsUrl: identityEdit.wsUrl.trim(),
                  }).then((res) => {
                    if (res.ok) {
                      setIdentityEdit(null);
                      toast('Identity updated');
                    } else {
                      setIdentityError(res.error);
                    }
                  });
                }}
                className="rounded bg-primary-base px-2 py-1 text-primary-on hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary-base"
                data-testid="settings-identity-save"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setIdentityEdit(null);
                  setIdentityError(null);
                }}
                className="rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
                data-testid="settings-identity-cancel"
              >
                Cancel
              </button>
            </div>
            {identityError !== null ? (
              <p
                className="col-span-2 text-rose-300"
                data-testid="settings-identity-error"
              >
                {identityError}
              </p>
            ) : null}
          </div>
        )}
        <div className="mt-3 flex items-center gap-2 text-xs">
          <label className="text-slate-400" htmlFor="settings-displayName">
            Display name
          </label>
          <input
            id="settings-displayName"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayNameLocal(e.target.value)}
            placeholder="e.g. Stage A — DC fast"
            className="rounded border border-slate-600 bg-slate-950 px-2 py-1 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
            data-testid="settings-displayName-input"
          />
          <button
            type="button"
            onClick={handleSaveName}
            className="rounded bg-primary-base px-2 py-1 text-primary-on hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary-base"
            data-testid="settings-displayName-save"
          >
            {/* VC-F — labelled so it isn't a second identical green "Save" next
                to the cpid/wsUrl edit-mode Save. */}
            Save name
          </button>
        </div>
        <p className="mt-3 text-[10px] text-slate-500" data-testid="settings-yonix">
          VirtualCharger.app — by{' '}
          <a
            href="https://yonix.sk"
            target="_blank"
            rel="noopener noreferrer"
            className="text-text-link hover:text-primary-base"
          >
            Yonix
          </a>
        </p>
      </section>

      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <h2 className="mb-2 text-sm font-medium text-slate-100">Topology</h2>
        <TopologyEditor />
      </section>

      <LocalAuthListSection />

      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <header className="mb-2 flex items-baseline justify-between">
          <h2 className="text-sm font-medium text-slate-100">Configuration keys</h2>
          <span className="text-[10px] text-slate-500" data-testid="config-count">
            {pluralize(entries.length, 'key')}
          </span>
        </header>
        <ConfigurationTable
          entries={entries}
          groupBy={isV2x(ocppVersion) ? 'component' : 'section'}
          onSetValue={(key, value) => {
            void setValue(stationId, key, value);
          }}
          onResetToDefault={(key) => {
            void resetToDefault(stationId, key);
          }}
          onDelete={(key) => {
            void deleteKey(stationId, key);
          }}
        />
      </section>

      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <h2 className="mb-2 text-sm font-medium text-slate-100">Maintenance</h2>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              void handleClearStale();
            }}
            className="rounded bg-amber-700 px-2 py-1 text-amber-50 hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-300 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="settings-clear-stale"
          >
            Clear all stale transactions
          </button>
          {clearedCount !== null ? (
            <span className="text-slate-400" data-testid="settings-cleared-count">
              {clearedCount} transaction(s) marked orphaned.
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleSoftReset}
            className="rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
            data-testid="settings-soft-reset"
          >
            Soft reset (keep identity)
          </button>
          <button
            type="button"
            onClick={handleFullReset}
            className="rounded bg-rose-700 px-2 py-1 text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300"
            data-testid="settings-full-reset"
          >
            Full reset (delete station)
          </button>
        </div>
      </section>

      {isV2x(ocppVersion) ? (
        <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
          <h2 className="mb-2 text-sm font-medium text-slate-100">Diagnostics (OCPP 2.x)</h2>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => {
                void fireDiagnosticEvent().then(() => toast('NotifyEvent sent'));
              }}
              className="rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
              data-testid="settings-fire-notify-event"
            >
              Send test NotifyEvent
            </button>
            <span className="text-slate-500">
              Emits a synthetic device event to the CSMS (check the Event Log).
            </span>
          </div>
        </section>
      ) : null}

      <FirmwareSection />

      <DiagnosticsSection />
    </div>
  );
}
